import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import CustomButton from "../../../pages/CustomButton";
import { formatDate } from "../../../utils/formatDate";

/* ================= DATE FORMATTER ================= */
const toYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const ProductSalesModal = ({ employeeId, date, onClose, customUrl }) => {
  const [products, setProducts] = useState([]);
  const [editedProducts, setEditedProducts] = useState([]);
  const [tableType, setTableType] = useState(""); // temptation | product
  const [temptationId, setTemptationId] = useState(null);

  const [totals, setTotals] = useState({ amount: 0, quantity: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [temptationPOB, setTemptationPOB] = useState(0);

  /* ================= FETCH DATA ================= */
  const fetchData = useCallback(async () => {
    if (!employeeId || !date) return;

    setLoading(true);
    setError(null);

    try {
      const formattedDate = toYMD(date);
      const url = customUrl
        ? customUrl
        : `${BASE_URL}/api/reports/employee-daily-product-sales?employee_id=${employeeId}&selected_date=${formattedDate}`;

      const res = await axios.get(url, { headers: getAuthHeaders() });

      setTemptationPOB(Number(res.data?.total_pob || 0));
      setTemptationId(res.data?.temptation_id || null);

      const apiProducts = res.data?.products || [];

      const isTemptation = apiProducts.some(
        (p) => p.temptation || p.current_mrp,
      );
      setTableType(isTemptation ? "temptation" : "product");

      /* ================= NORMALIZATION (MAIN FIX) ================= */
      const normalized = apiProducts.map((p, i) => {
        const quantity = p.quantity ?? p.qty ?? 0;
        const price = p.price ?? p.rate ?? p.current_mrp ?? 0;
        const amount = p.total ?? p.amount ?? p.net ?? quantity * price;

        return {
          sno: p.s_no ?? i + 1,
          product_id: p.code ?? p.s_no ?? i + 1,

          // ✅ PRODUCT SALES KE CORRECT KEYS
          code: p.code ?? "",
          mapping_code: p.mapping_code ?? "",

          product: p.product_name ?? p.product ?? p.temptation ?? "-",

          packaging: p.packaging ?? "-",
          price: Number(price),
          quantity: Number(quantity),
          amount: Number(amount),

          // ✅ ADD THESE
          scheme: Number(p.scheme ?? 0),
          net: Number(p.net ?? amount),
        };
      });

      setProducts(normalized);

      /* ================= TEMPTATION EDIT STATE ================= */
      if (isTemptation) {
        const editList = normalized.map((p) => ({
          product_id: p.product_id,
          product_name: p.product,
          quantity: p.quantity,
        }));

        setEditedProducts(editList);

        setTotals({
          quantity: editList.reduce((s, p) => s + Number(p.quantity || 0), 0),
          amount: editList.reduce(
            (s, p) =>
              s +
              Number(p.quantity || 0) *
                Number(
                  normalized.find((x) => x.product_id === p.product_id)
                    ?.price || 0,
                ),
            0,
          ),
        });
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [employeeId, date, customUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ================= TOTAL RECALC ================= */
  const recalculateTotals = (list) => ({
    quantity: list.reduce((s, p) => s + Number(p.quantity || 0), 0),
    amount: list.reduce(
      (s, p) =>
        s +
        Number(p.quantity || 0) *
          Number(
            products.find((x) => x.product_id === p.product_id)?.price || 0,
          ),
      0,
    ),
  });

  /* ================= UPDATE TEMPTATION ================= */
  const handleSubmitTemptation = async () => {
    if (!temptationId) return alert("Temptation ID missing");

    try {
      setSaving(true);

      const payload = {
        employee_id: employeeId,
        products_details: editedProducts.map((p) => ({
          product_id: p.product_id,
          product_name: p.product_name,
          quantity: Number(p.quantity),
        })),
      };

      await axios.put(`${BASE_URL}/api/temptations/${temptationId}`, payload, {
        headers: getAuthHeaders(),
      });

      await fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to update temptation");
    } finally {
      setSaving(false);
    }
  };

  /* ================= TABLE ================= */
  const renderTable = () => (
    <table className="w-full text-sm border-collapse">
      <thead className="bg-blue-500 text-white">
        <tr>
          <th>S.No</th>

          {/* PRODUCT SALES EXTRA COLUMNS */}
          {tableType === "product" && (
            <>
              <th>Code</th>
              <th>Mapping Code</th>
            </>
          )}

          <th>{tableType === "temptation" ? "Temptation" : "Product"}</th>

          {tableType === "temptation" && <th>Packaging</th>}

          <th className="text-right">MRP</th>
          <th className="text-right">Quantity</th>
          <th className="text-right">Total</th>

          {/* PRODUCT SALES EXTRA */}
          {tableType === "product" && (
            <>
              <th className="text-right">Scheme</th>
              <th className="text-right">Net</th>
            </>
          )}
        </tr>
      </thead>

      <tbody>
        {products.map((row, index) => {
          const scheme = row.scheme;
          const totalAmount = row.amount;
          const net = row.net;

          return (
            <tr key={row.product_id}>
              <td>{index + 1}</td>

              {/* PRODUCT SALES */}
              {tableType === "product" && (
                <>
                  <td>{row.code}</td>
                  <td>{row.mapping_code}</td>
                </>
              )}

              <td>{row.product}</td>

              {tableType === "temptation" && <td>{row.packaging}</td>}

              <td className="text-right">{row.price}</td>

              <td className="text-right">
                {tableType === "temptation" ? (
                  <input
                    type="number"
                    min="0"
                    value={
                      editedProducts.find(
                        (p) => p.product_id === row.product_id,
                      )?.quantity ?? row.quantity
                    }
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setEditedProducts((prev) => {
                        const updated = prev.map((p) =>
                          p.product_id === row.product_id
                            ? { ...p, quantity: v }
                            : p,
                        );
                        setTotals(recalculateTotals(updated));
                        return updated;
                      });
                    }}
                    className="w-20 border px-2 py-1 text-right"
                  />
                ) : (
                  row.quantity
                )}
              </td>

              {/* TOTAL COLUMN (FIXED & PRESENT) */}
              <td className="text-right">{totalAmount.toFixed(0)}</td>

              {/* PRODUCT SALES ONLY */}
              {tableType === "product" && (
                <>
                  <td className="text-right">{Math.floor(scheme)}</td>
                  <td className="text-right">{Math.floor(net)}</td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>

      {tableType === "temptation" && (
        <tfoot>
          <tr className="bg-blue-600 text-white font-semibold">
            <td colSpan={4} className="text-center">
              Total
            </td>
            <td className="text-right">{totals.quantity}</td>
            <td className="text-right">{totals.amount.toFixed(0)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );

  /* ================= MODAL ================= */
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full">
        <div className="p-4 border-b font-semibold text-center">
          {tableType === "temptation" ? (
            <>
              <span className="text-blue-600 font-bold">
                POB: ₹{temptationPOB.toLocaleString()}
              </span>
              {" – "}
              {formatDate(date)}
            </>
          ) : (
            <>
              <span className="text-blue-600 font-bold">Product Sales</span>
              {" – "}
              {formatDate(date)}
            </>
          )}
        </div>

        <div className="p-4 max-h-[70vh] overflow-auto">
          {loading ? (
            <div className="text-center">Loading...</div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : (
            renderTable()
          )}
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          {tableType === "temptation" && (
            <CustomButton
              label={saving ? "Saving..." : "Submit"}
              variant="save"
              onClick={handleSubmitTemptation}
              disabled={saving}
            />
          )}
          <CustomButton label="Close" variant="cancel" onClick={onClose} />
        </div>
      </div>
    </div>
  );
};

export default ProductSalesModal;
